import React from 'react';
import Grid from 'components/elements/Grid';
import FieldSetWithTitle from 'shared/FieldSetWithTitle/components/FieldSetWithTitle';
import { ContractTextInput } from '../ContractFields';
import { formatFutureFixingDate, nextFutureFixingDate } from 'contracts/contractUtils';
import { IField } from 'components/shared/form/IField';
import { usePriceContext } from 'contracts/utils/priceContext';
import Markups from '../Markups';
import DifferentialPriceFields from '../DifferentialPriceFields';
import OpportunityFixedPrice from '../OpportunityFixedPrice';

export interface PriceProps {
  showMarkups: boolean;
  priceTypeField?: IField;
  futurePriceFixingDateField?: IField;
  fixationRightsField?: IField;
  shipmentDate: Date;
  contractType?: string;
}

export function Price({
  showMarkups,
  futurePriceFixingDateField,
  fixationRightsField,
  shipmentDate,
  contractType,
}: PriceProps) {
  const {
    fixedPriceType,
    fixedPrice,
    priceUsdLb,
    differentialPrice,
    fobPrice,
    fobPriceUsdLb,
    internalContractPrice,
    contractPricesCustomerToUsdRate,
    measurementRate,
    sameCurrencyUnitofMeasure,
    priceBelowSimulator,
    fobBelowSimulator,
  } = usePriceContext();

  const handleFobChange = (value) => {
    if (contractType === 'InternalContract') {
      fobPrice?.set(value);
      return;
    }
    if (contractType != 'LocalContract') {
      fobPriceUsdLb?.set(String(value));
      if (fixedPriceType?.value == 'fixed') {
        fobPrice?.set(
          String(
            (Number(value) / Number(measurementRate)) *
              Number(contractPricesCustomerToUsdRate?.value)
          )
        );
      } else if (fixedPriceType?.value == 'differential') {
        fobPrice?.set(value);
      }
    }
  };

  const handleLocalChange = (value) => {
    if (contractType === 'LocalContract') {
      fixedPrice?.set(value);
      const usdKgRate = value * Number(contractPricesCustomerToUsdRate?.value);
      const priceFinal = usdKgRate / Number(measurementRate);
      priceUsdLb?.set(String(priceFinal.toFixed(4)));
    }
  };

  return (
    <FieldSetWithTitle title={I18n.translate('contracts.form.price')}>
      <Grid>
        <ContractTextInput field={fixedPriceType?.field} />

        {fixedPriceType?.value == 'fixed' && <OpportunityFixedPrice />}

        {fobPrice && contractType === 'InternalContract' && (
          <ContractTextInput
            field={fobPrice.field}
            value={fobPrice.value}
            onChange={(value) => {
              handleFobChange(value);
            }}
            type='number'
            step='0.0001'
            min='0.0001'
          />
        )}

        {contractType != 'LocalContract' && (
          <>
            {fobPriceUsdLb && (
              <ContractTextInput
                field={fobPriceUsdLb.field}
                value={fobPriceUsdLb.value}
                onChange={(value) => {
                  handleFobChange(value);
                }}
                externalValid={!fobBelowSimulator}
                type='number'
                step='0.0001'
                min='0.0001'
              />
            )}

            {fixedPriceType?.value == 'fixed' &&
              fobPrice &&
              !sameCurrencyUnitofMeasure && (
                <ContractTextInput
                  field={fobPrice.field}
                  value={fobPrice.value}
                  onChange={fobPrice.set}
                  type='number'
                  step='0.0001'
                  min='0.0001'
                />
              )}
          </>
        )}

        {showMarkups && <Markups />}

        {fixedPriceType?.value == 'fixed' &&
          contractType != 'LocalContract' &&
          fixedPrice && (
            <>
              {priceUsdLb && !sameCurrencyUnitofMeasure && (
                <ContractTextInput
                  field={priceUsdLb.field}
                  value={priceUsdLb.value}
                  onChange={priceUsdLb.set}
                  externalValid={!priceBelowSimulator}
                  type='number'
                  step='0.0001'
                  min='0.0001'
                />
              )}
              <ContractTextInput
                field={fixedPrice.field}
                value={fixedPrice.value}
                onChange={fixedPrice.set}
                externalValid={!priceBelowSimulator}
                type='number'
                step='0.0001'
                min='0.0001'
              />
              <ContractTextInput
                field={futurePriceFixingDateField}
                onChange={() => true}
                value={formatFutureFixingDate(nextFutureFixingDate(shipmentDate, false))}
              />
            </>
          )}

        {contractType === 'LocalContract' && fixedPrice && (
          <>
            <ContractTextInput
              field={fixedPrice.field}
              value={fixedPrice.value}
              onChange={(value) => {
                handleLocalChange(value);
              }}
              type='number'
              step='0.0001'
              min='0.0001'
            />
            {priceUsdLb && !sameCurrencyUnitofMeasure && (
              <ContractTextInput
                field={priceUsdLb.field}
                value={priceUsdLb.value}
                onChange={priceUsdLb.set}
                type='number'
                step='0.0001'
                min='0.0001'
              />
            )}
            <ContractTextInput
              field={futurePriceFixingDateField}
              onChange={() => true}
              value={formatFutureFixingDate(nextFutureFixingDate(shipmentDate, false))}
            />
          </>
        )}

        {fixedPriceType?.value == 'differential' && differentialPrice && (
          <DifferentialPriceFields
            futurePriceFixingDateField={futurePriceFixingDateField}
            fixationRightsField={fixationRightsField}
            shipmentDate={shipmentDate}
          />
        )}

        {internalContractPrice && (
          <ContractTextInput field={internalContractPrice.field}></ContractTextInput>
        )}
      </Grid>
    </FieldSetWithTitle>
  );
}

export default Price;
